<template>
  <button
    :class="[
      'DateChip',
      {
        'is-selected': selected,
        'is-disabled': disabled,
      },
    ]"
    :disabled="disabled"
    :aria-pressed="selected"
    @click="handleClick"
  >
    <span class="DateChip-text">
      {{ dateLineOne }}
    </span>
    <span class="DateChip-text">
      {{ dateLineTwo }}
    </span>
    <span v-if="subtitle" class="DateChip-smallText">
      {{ subtitle }}
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { format } from 'date-fns';
import { useI18n } from '@cvo/i18n/composables/useI18n';

export interface DateChipProps {
  /** The date to display */
  date: Date;
  /** Whether this date is selected */
  selected?: boolean;
  /** Text displaying under the date label */
  subtitle?: string;
  /** Whether this date is disabled */
  disabled?: boolean;
}

const props = defineProps<DateChipProps>();

const emit = defineEmits<{
  click: [date: Date];
}>();

const { t } = useI18n();

const dateLineOne = computed(() => {
  return format(props.date, t('ui.date.formats.weekdayDay'));
});

const dateLineTwo = computed(() => {
  return format(props.date, t('ui.date.formats.month'));
});

function handleClick() {
  if (!props.disabled) {
    emit('click', props.date);
  }
};
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .DateChip {
    @include layout.flex-center;
    background-color: vars.$atoms-chips-chip-date-default-bg-color;
    border: vars.$atoms-chips-chip-date-default-border-stroke solid vars.$atoms-chips-chip-date-default-border-color;
    border-radius: vars.$atoms-chips-chip-date-default-border-radius;
    padding: vars.$dimensions-d-3;
    cursor: pointer;
    transition: all 0.2s ease;
    box-sizing: border-box;

    color: vars.$atoms-chips-chip-date-default-text-color;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
    @include text.text(sm, vars.$typography-weight-strong);

    @include responsive.mobile {
      padding: vars.$dimensions-d-1 vars.$dimensions-d-2;
    }

    &:hover:not(.is-disabled) {
      background-color: vars.$atoms-chips-chip-date-hover-bg-color;
      border: vars.$atoms-chips-chip-date-hover-border-stroke solid vars.$atoms-chips-chip-date-hover-border-color;
      border-radius: vars.$atoms-chips-chip-date-hover-border-radius;

      .DateChip-text {
        color: vars.$atoms-chips-chip-date-hover-text-color;
      }
    }

    &:focus {
      outline: 2px solid vars.$color-brand-secondary-900;
      outline-offset: 2px;
    }

    &.is-selected {
      background-color: vars.$atoms-chips-chip-date-active-bg-color;
      border: vars.$atoms-chips-chip-date-active-border-stroke solid vars.$atoms-chips-chip-date-active-border-color;
      border-radius: vars.$atoms-chips-chip-date-active-border-radius;
      color: vars.$atoms-chips-chip-date-active-text-color;
    }

    &.is-disabled {
      background-color: vars.$atoms-chips-chip-date-disabled-bg-color;
      border: vars.$atoms-chips-chip-date-disabled-border-stroke solid vars.$atoms-chips-chip-date-disabled-border-color;
      border-radius: vars.$atoms-chips-chip-date-disabled-border-radius;
      cursor: not-allowed;
      color: vars.$atoms-chips-chip-date-disabled-text-color;
    }

    &-text {
      margin: 0;
      display: block;
    }

    &-smallText {
      @extend .DateChip-text;
      @include text.text(xs);
      font-weight: vars.$typography-weight-strong;
    }
  }
}
</style>
